import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import { api } from '../../api/client';
import { AppDataGrid, type AppColumn, gridExportMatrix } from '../../components/AppDataGrid';
import { AppToolbar, ExportMenu } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useHotkeys } from '../../components/hotkeys';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';

// ─────────────────────────────────────────────────────────────────────────────
// Settings ▸ Field Template ▸ Field Tests  (list + batch Edit)
//
// Port of the legacy ALPACA XAF "Field Tests" (TestMethod_ListView_FieldTest over
// the shared dbo.TestMethod).  The LIST shows only the field tests (IsFieldTest=1);
// clicking Edit loads EVERY live TestMethod with a per-row IsFieldTest checkbox the
// user toggles and Saves (legacy's inline-edit grid + Save).  The full TestMethod
// master lives in "Test Code" — this screen only owns the IsFieldTest flag.
//
// Bespoke page (not the shared ModuleListView): the Edit mode swaps the whole data
// set (13 → 165) and adds an editable checkbox column, which the generic list view
// has no slot for.  Still composed from the shared FormTitle / AppToolbar /
// AppDataGrid / GridSearch / useConfirm / useNotify, so styling, hotkeys, the
// confirm+success dialogs and the unsaved-changes guard are all inherited.
// (Owner request 2026-10-02.)
// ─────────────────────────────────────────────────────────────────────────────

interface FieldTestRow {
  oid: string;
  matrix: string | null;
  test: string | null;
  testCode: string | null;
  category: string | null;
  method: string | null;
  isFieldTest: boolean;
}

export default function FieldTestsPage() {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [mode, setMode] = useState<'list' | 'edit'>('list');
  const [listRows, setListRows] = useState<FieldTestRow[]>([]);
  const [editRows, setEditRows] = useState<FieldTestRow[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [original, setOriginal] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // Page-level maximize (owner report 2026-10-14): the grid's own overlay maximize
  // covered the search row, which hid the Restore button. Instead the Zoom button
  // toggles this flag, which HIDES the breadcrumb + toolbar while the grid card fills
  // the area and the search row (with Restore) stays visible. Esc restores.
  const [maximized, setMaximized] = useState(false);
  useEffect(() => {
    if (!maximized) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMaximized(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);

  // Dirty when the Edit grid's checkbox state differs from what was loaded
  // (standing rule 15 — unsaved changes are never lost silently).
  const dirty = mode === 'edit'
    && (checked.size !== original.size || [...checked].some((id) => !original.has(id)));
  useUnsavedGuard(dirty);

  // ── List mode — the field tests (IsFieldTest=1) ──
  const loadList = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/field-tests');
      setListRows((res.data?.data ?? []) as FieldTestRow[]);
      setLoadMs(Math.round(performance.now() - t0));
    } catch {
      notify.error('Could not load the field tests.');
      setListRows([]);
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => { void loadList(); }, [loadList]);

  // ── Edit mode — load EVERY live test + its IsFieldTest flag ──
  const enterEdit = useCallback(async () => {
    setLoadingEdit(true);
    try {
      const res = await api.get('/api/v1/field-tests/all');
      const rows = (res.data?.data ?? []) as FieldTestRow[];
      setEditRows(rows);
      const init = new Set(rows.filter((r) => r.isFieldTest).map((r) => r.oid));
      setChecked(new Set(init));
      setOriginal(init);
      setSearch('');
      setMode('edit');
    } catch {
      notify.error('Could not load the tests to edit.');
    } finally {
      setLoadingEdit(false);
    }
  }, [notify]);

  const toggle = useCallback((oid: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid); else next.add(oid);
      return next;
    });
  }, []);

  const save = useCallback(async () => {
    const ok = await confirm({ title: 'Do you want to Save?', message: 'Do you want to Save?', confirmLabel: 'Yes', cancelLabel: 'No' });
    if (!ok) return;
    setSaving(true);
    try {
      await api.put('/api/v1/field-tests', { fieldTestOids: [...checked] });
      notify.success('Saved successfully.');
      setOriginal(new Set(checked));   // clear the dirty flag before leaving
      setMode('list');
      await loadList();
    } catch {
      notify.error('Could not save the field tests.');
    } finally {
      setSaving(false);
    }
  }, [checked, confirm, notify, loadList]);

  const cancelEdit = useCallback(async () => {
    if (dirty) {
      const ok = await confirm({
        title: 'Unsaved changes',
        message: 'Unsaved changes will be lost. Do you want to proceed?',
        confirmLabel: 'OK', cancelLabel: 'Cancel',
      });
      if (!ok) return;
    }
    setMode('list');
  }, [dirty, confirm]);

  // Save / Cancel keyboard shortcuts for the bespoke action row (standing rule 14).
  useHotkeys(mode === 'edit'
    ? [
        { combo: 'mod+s', label: 'Save', group: 'Field Tests', disabled: saving, run: () => { void save(); } },
        { combo: 'escape', label: 'Cancel', group: 'Field Tests', run: () => { void cancelEdit(); } },
      ]
    : []);

  // ── Columns ──
  const listColumns: AppColumn<FieldTestRow>[] = useMemo(() => [
    { field: 'matrix', header: 'Matrix', width: 150, render: (r) => r.matrix ?? '', text: (r) => r.matrix ?? '' },
    { field: 'test', header: 'Test', width: 260, render: (r) => r.test ?? '', text: (r) => r.test ?? '' },
    { field: 'testCode', header: 'TestCode', width: 130, render: (r) => r.testCode ?? '', text: (r) => r.testCode ?? '' },
    { field: 'category', header: 'Category', width: 170, render: (r) => r.category ?? '', text: (r) => r.category ?? '' },
    { field: 'method', header: 'Method', width: 180, render: (r) => r.method ?? '', text: (r) => r.method ?? '' },
  ], []);

  const editColumns: AppColumn<FieldTestRow>[] = useMemo(() => [
    ...listColumns,
    {
      field: 'isFieldTest', header: 'IsFieldTest', width: 120, align: 'center',
      render: (r) => (
        <input
          type="checkbox"
          checked={checked.has(r.oid)}
          onChange={() => toggle(r.oid)}
          aria-label={`IsFieldTest ${r.test ?? ''}`}
        />
      ),
      text: (r) => (checked.has(r.oid) ? 'Yes' : ''),
      compare: (a, b) => (checked.has(a.oid) ? 1 : 0) - (checked.has(b.oid) ? 1 : 0),
    },
  ], [listColumns, checked, toggle]);

  // ── Client-side search (mirrors the legacy Search box) ──
  const filterRows = useCallback((rows: FieldTestRow[]) => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.matrix, r.test, r.testCode, r.category, r.method]
        .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [search]);

  const visibleList = useMemo(() => filterRows(listRows), [filterRows, listRows]);
  const visibleEdit = useMemo(() => filterRows(editRows), [filterRows, editRows]);

  const selectedCount = useMemo(() => editRows.filter((r) => checked.has(r.oid)).length, [editRows, checked]);

  return (
    <div className="bl-page">
      {!maximized && (
      <div className="bl-top">
        <div className="dp-page-hdr" style={{ padding: '6px 0 0' }}>
          <div className="dp-breadcrumb">
            <span>Home</span>
            <i className="ti ti-chevron-right" />
            <span>Settings</span>
            <i className="ti ti-chevron-right" />
            {/* subgroup node (owner request 2026-10-14) */}
            <span>Field Template</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Field Tests</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="set-field-tests" auditFormName="Field Tests" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            {mode === 'list'
              ? `${visibleList.length} field test${visibleList.length === 1 ? '' : 's'}`
              : `Editing — ${selectedCount} of ${editRows.length} marked as field tests`}
          </div>
        </div>
      </div>
      )}

      <div className="bl-grid-area">
        {!maximized && (mode === 'list' ? (
          // List: Edit sits BEFORE Refresh (AI canonical order) + Export to.
          <AppToolbar
            onEdit={() => void enterEdit()}
            editDisabled={loadingEdit}
            onRefresh={() => void loadList()}
            refreshing={loading || loadingEdit}
            exportData={{ filename: 'Field Tests', ...gridExportMatrix(visibleList, listColumns) }}
          />
        ) : (
          // Edit: Save · Cancel (left slot) + Refresh (reload all) + Export to.
          <AppToolbar
            left={
              <>
                <button type="button" className="ca-btn ca-btn-solid" onClick={() => void save()} disabled={saving}>
                  💾 Save
                </button>
                <button type="button" className="ca-btn" onClick={() => void cancelEdit()} disabled={saving}>
                  ↩ Cancel
                </button>
              </>
            }
            onRefresh={() => void enterEdit()}
            refreshing={loadingEdit}
            exportData={{ filename: 'Field Tests', ...gridExportMatrix(visibleEdit, editColumns) }}
          />
        ))}

        {/* ONE row OUTSIDE the grid card (CityPage pattern): Search on the left,
            Export (share) + Zoom (maximize) on the right. The grid's own header
            tools are hidden (hideHeaderTools); the Zoom button drives PAGE-LEVEL
            maximize (above), so this row — and its Restore button — stays visible. */}
        <GridSearch
          value={search}
          onChange={setSearch}
          placeholder="Search…"
          style={maximized ? { padding: '4px 0 7px' } : { paddingTop: 10 }}
          right={
            <div className="list-search-actions">
              <ExportMenu
                iconOnly
                triggerClassName="adg-max-btn"
                filename="Field Tests"
                {...gridExportMatrix(
                  mode === 'list' ? visibleList : visibleEdit,
                  mode === 'list' ? listColumns : editColumns,
                )}
              />
              <button
                type="button"
                className="adg-max-btn"
                onClick={() => setMaximized((v) => !v)}
                title={maximized ? 'Restore' : 'Maximize'}
                aria-label={maximized ? 'Restore' : 'Maximize'}
              >
                <i className={`ti ti-arrows-${maximized ? 'minimize' : 'maximize'}`} />
              </button>
            </div>
          }
        />

        <div className="bl-card bl-card-flex">
          {mode === 'list' ? (
            <AppDataGrid<FieldTestRow>
              rows={visibleList}
              columns={listColumns}
              getRowId={(r) => r.oid}
              loading={loading}
              hideHeaderTools
              initialSort={{ key: 'testCode', dir: 'asc' }}
              emptyText="No field tests."
            />
          ) : (
            <AppDataGrid<FieldTestRow>
              rows={visibleEdit}
              columns={editColumns}
              getRowId={(r) => r.oid}
              loading={loadingEdit}
              hideHeaderTools
              initialSort={{ key: 'testCode', dir: 'asc' }}
              emptyText="No tests."
            />
          )}
        </div>
      </div>
    </div>
  );
}
